/**
 * HopperSystem — Moves items between containers automatically.
 *
 * A hopper pulls items from the container above it and pushes items
 * to the container below it, at a rate of 1 item per 0.4 seconds.
 *
 * Supported containers: CHEST, FURNACE, DROPPER, DISPENSER, other HOPPER.
 */
import { BlockId } from './BlockType.js';

const TRANSFER_INTERVAL = 0.4; // seconds between transfers
const HOPPER_SLOTS = 5;

export class HopperSystem {
  constructor(world, tileEntities) {
    this.world = world;
    this.tileEntities = tileEntities;
    this._timer = 0;
    this._hoppers = new Map(); // "x,y,z" -> { slots: Array(5) }
  }

  /**
   * Get or create hopper data at position.
   */
  getHopper(x, y, z) {
    const key = `${x},${y},${z}`;
    let hopper = this._hoppers.get(key);
    if (!hopper) {
      hopper = { slots: new Array(HOPPER_SLOTS).fill(null) };
      this._hoppers.set(key, hopper);
    }
    return hopper;
  }

  /**
   * Called every frame. Processes item transfers at TRANSFER_INTERVAL.
   */
  update(dt) {
    this._timer += dt;
    if (this._timer < TRANSFER_INTERVAL) return;
    this._timer = 0;

    // Find all hoppers in the world (from tileEntities that have type 'hopper')
    for (const [key, data] of this.tileEntities.data) {
      if (data.type !== 'hopper') continue;
      const [x, y, z] = key.split(',').map(Number);
      const block = this.world.getBlock(x, y, z);
      if (block !== BlockId.HOPPER) {
        this.tileEntities.data.delete(key);
        continue;
      }

      // Pull from above
      this._pullFromAbove(x, y, z, data);

      // Push to below
      this._pushToBelow(x, y, z, data);
    }
  }

  /**
   * Pull one item from container above the hopper.
   */
  _pullFromAbove(x, y, z, hopperData) {
    const aboveBlock = this.world.getBlock(x, y + 1, z);
    if (aboveBlock !== BlockId.CHEST && aboveBlock !== BlockId.HOPPER) return;

    const aboveKey = `${x},${y + 1},${z}`;
    const aboveData = this.tileEntities.data.get(aboveKey);
    if (!aboveData || !aboveData.slots) return;

    // Find first non-null slot in source
    for (let i = 0; i < aboveData.slots.length; i++) {
      const srcSlot = aboveData.slots[i];
      if (!srcSlot || srcSlot.count <= 0) continue;

      // Find space in hopper
      const hopperSlots = hopperData.slots || new Array(HOPPER_SLOTS).fill(null);
      for (let j = 0; j < hopperSlots.length; j++) {
        const dstSlot = hopperSlots[j];
        if (!dstSlot) {
          // Empty slot — move 1 item
          hopperSlots[j] = { ...srcSlot, count: 1 };
          srcSlot.count--;
          if (srcSlot.count <= 0) aboveData.slots[i] = null;
          hopperData.slots = hopperSlots;
          this.tileEntities.save();
          return;
        } else if (this._canStack(dstSlot, srcSlot) && dstSlot.count < 64) {
          dstSlot.count++;
          srcSlot.count--;
          if (srcSlot.count <= 0) aboveData.slots[i] = null;
          this.tileEntities.save();
          return;
        }
      }
      break; // tried first available item, no space
    }
  }

  /**
   * Push one item from hopper to container below.
   */
  _pushToBelow(x, y, z, hopperData) {
    const belowBlock = this.world.getBlock(x, y - 1, z);
    if (belowBlock !== BlockId.CHEST && belowBlock !== BlockId.HOPPER &&
        belowBlock !== BlockId.FURNACE && belowBlock !== BlockId.FURNACE_LIT) return;

    const belowKey = `${x},${y - 1},${z}`;
    let belowData = this.tileEntities.data.get(belowKey);
    if (!belowData) {
      // Create container data if it doesn't exist
      if (belowBlock === BlockId.CHEST) {
        belowData = this.tileEntities.getChest(x, y - 1, z);
      } else {
        return;
      }
    }
    if (!belowData.slots) return;

    const hopperSlots = hopperData.slots || [];

    // Find first item in hopper
    for (let i = 0; i < hopperSlots.length; i++) {
      const srcSlot = hopperSlots[i];
      if (!srcSlot || srcSlot.count <= 0) continue;

      // Find space in destination
      for (let j = 0; j < belowData.slots.length; j++) {
        const dstSlot = belowData.slots[j];
        if (!dstSlot) {
          belowData.slots[j] = { ...srcSlot, count: 1 };
          srcSlot.count--;
          if (srcSlot.count <= 0) hopperSlots[i] = null;
          this.tileEntities.save();
          return;
        } else if (this._canStack(dstSlot, srcSlot) && dstSlot.count < 64) {
          dstSlot.count++;
          srcSlot.count--;
          if (srcSlot.count <= 0) hopperSlots[i] = null;
          this.tileEntities.save();
          return;
        }
      }
      break;
    }
  }

  _canStack(a, b) {
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    return false;
  }

  /**
   * Register a hopper block in tile entities.
   */
  registerHopper(x, y, z) {
    const key = `${x},${y},${z}`;
    this.tileEntities.data.set(key, {
      type: 'hopper',
      slots: new Array(HOPPER_SLOTS).fill(null),
    });
    this.tileEntities.save();
  }

  serialize() {
    return {};
  }

  deserialize() {
    // Hoppers are stored in tileEntities, no separate state needed
  }
}
